import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import ApiPath from "./ConstantApi";
import Config from "../Config";
import Storage from "../Helpers/Storage";
import { v4 as uuidv4 } from "uuid";

// Types
export interface SignInProps {
  email: string;
  password: string;
  /** Cloudflare Turnstile token, verified by the API before the login runs. */
  captchaToken?: string | null;
}

export interface SignUpProps {
  name: string;
  email: string;
  password: string;
  id?: string;
}

export interface SignUpRequest {
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  profilePic?: string;
  countryIsoCode?: string;
  countryDialCode?: string;
  password: string;
  loginMethod: string;

  preferences?: Preferences;
  status: "active" | "inactive" | "banned";
  /** Cloudflare Turnstile token, verified by the API before the account is made. */
  captchaToken?: string | null;
}

export interface Preferences {
  language: string;
  country: string;
  currency: string;
  notificationsEnabled: boolean;
}

export const getDeviceId = () => {
  let deviceId = Storage.get(`${Config.STORAGE_KEY}/device-id`);
  if (deviceId) {
    return JSON.parse(deviceId);
  }
  const newDeviceId = uuidv4();
  Storage.set(`${Config.STORAGE_KEY}/device-id`, JSON.stringify(newDeviceId));
  return newDeviceId;
};

// Create the API slice
export const AuthService = createApi({
  reducerPath: "AuthApi",
  baseQuery: fetchBaseQuery({
    baseUrl: Config.BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Content-Type", "application/json");
      headers.set("x-device-id", getDeviceId());

      /**
       * Send the session token when there is one.
       *
       * These endpoints serve signed-out visitors, which is why this slice has
       * never attached a token. But two of them are also used by a signed-in
       * customer: My Account verifying their email or phone, and Change
       * Password requesting a reset link. Those routes let an authenticated
       * request skip the captcha -- somebody with a session has already proved
       * who they are, and there is no captcha widget on those screens to solve.
       *
       * Without the header the server cannot tell the two apart, so it asked a
       * signed-in customer to complete a security check that was nowhere on the
       * page: "Please complete the security check and try again", with no way
       * to comply.
       *
       * Nothing changes for anonymous callers -- they have no token, so no
       * header is sent and the captcha is required exactly as before.
       */
      const state: any = getState();
      const accessToken = state?.AuthSlice?.accessToken;
      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    signIn: builder.mutation<any, SignInProps>({
      query: (reqBody) => ({
        url: `${ApiPath.signIn}`,
        method: "POST",
        body: reqBody,
      }),
    }),

    signUp: builder.mutation<any, SignUpRequest>({
      query: (reqBody) => ({
        url: `${ApiPath.signUp}`,
        method: "POST",
        body: reqBody,
      }),
    }),

    passwordReset: builder.mutation<any, any>({
      query: (reqBody) => ({
        url: `${ApiPath.signUp}`,
        method: "POST",
        body: reqBody,
      }),
    }),

    forgotPassword: builder.mutation<any, any>({
      query: (body) => ({
        url: `${ApiPath.forgotPassword}`,
        method: "POST",
        body: body,
      }),
    }),

    resetPassword: builder.mutation<any, any>({
      query: (body) => ({
        url: `${ApiPath.resetPassword}`,
        method: "POST",
        body: body,
      }),
    }),

    changePwdOTPRequest: builder.query<any, any>({
      query: () => ({
        url: `${ApiPath.changePassword.customerPwdOTPRequest}`,
        method: "GET",
      }),
    }),

    changePasswordAndValidate: builder.mutation<any, any>({
      query: (body) => ({
        url: `${ApiPath.changePassword.customerPwdOTPvalidate}`,
        method: "POST",
        body: body,
      }),
    }),

    requestOtp: builder.mutation<
      any,
      { email: string; phoneNumber: string; message: string }
    >({
      query: (body) => ({
        url: `${
          body.email ? ApiPath.requestOtp.email : ApiPath.requestOtp.mobile
        }`,
        method: "POST",
        body: body,
      }),
    }),

    verifyOtp: builder.mutation<
      any,
      { email: string; mobile: string; otp: string }
    >({
      query: (body) => ({
        url: `${
          body.email ? ApiPath.verifyOtp.email : ApiPath.verifyOtp.mobile
        }`,
        method: "POST",
        body: body,
      }),
    }),

    checkTokenExpiry: builder.mutation<any, { token: string }>({
      query: (body) => ({
        url: `${ApiPath.checkToken}`,
        method: "POST",
        body: body,
      }),
    }),

    // Social Login - Google
    googleSocialLogin: builder.query<any, Record<string, any>>({
      query: (params) => ({
        url: `${ApiPath.socialLogin.google}`,
        method: "GET",
        params,
      }),
    }),

    appleSocialLogin: builder.query<any, Record<string, any>>({
      query: (params) => ({
        url: `${ApiPath.socialLogin.apple}`,
        method: "GET",
        params,
      }),
    }),
  }),
});

// Export hooks for the API
export const {
  useSignInMutation,
  usePasswordResetMutation,
  useSignUpMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePwdOTPRequestQuery,
  useChangePasswordAndValidateMutation,
  useRequestOtpMutation,
  useVerifyOtpMutation,
  useCheckTokenExpiryMutation,
  useLazyGoogleSocialLoginQuery,
  useLazyAppleSocialLoginQuery,
} = AuthService;
